/** The tour record editor's binding of the records core (plan §3, D3, D9) — what FieldSlot
 *  and the Ledger need to know about a tour that the catalog alone does not say.
 *
 *  - Verdicts: while the tour is import-bound, fieldSpec's `importOwned` fields are
 *    `may_overwrite` and its `importForced` ones `overwrites` ("reset on every import
 *    unless the XML sets it") — the sync icon and the "next update will undo this" mark.
 *  - Editability: `console_tours_manage`, a tour with an owning operator, not deleted. A
 *    context key stored as something other than text stays read-only until its widget
 *    can keep that type (P3) — never blanked, never deleted. An alias an import maintains
 *    (`alias.managed`, as the draft has it) is read-only until taken over: the take-over
 *    is a draft change, and the row is editable from that step on (the save applies the
 *    whole row); handing one back locks it again. Reverting a take-over reverts the row's
 *    edits with it (they would not be saved on a managed row).
 *  - Ledger: one group per tour; rows of an object's fields carry its name ("Meeting
 *    point · Address"). No Description (a tour PATCH cannot carry one). No confirmation
 *    step: a tour save sends no guest event and writes no outside system.
 *  - Preview: the local plan (`localPlan`, D9). Apply: `useTourApply` (409 refetch, drift,
 *    one silent retry; 400 mapping).
 *  - Rows the draft creates (negative ids) take their defaults from the collection specs.
 *  - Collection rows (`alias:12`, `tagcat:5`) share the tour's ledger group; a row reads
 *    "Alias GYG-1 · Matching: parked → matching", a membership list "External IDs: 2 IDs
 *    → 3 (+ NEW-1)". Reverting a membership list also reverts the entries of the rows it
 *    added (`cascadeTargets`). While the operator does not allow per-tour tag categories
 *    (`tag_categories_editable`), the tag-category rows and their list are read-only with
 *    the reason — the PATCH would drop them without a word.
 *  - The itinerary (P4, `itinerary/ledger.ts`): one ledger group per day ("Itinerary ·
 *    Day 3 — 2 stops changed") in the tour's day order, rows naming their stop / activity,
 *    lists as counts with what joined and left; reverting a list takes back the rows it
 *    added and the list a saved row was moved out of.
 */
import { useMemo, useRef } from 'react';

import type { TourDetail } from '@/api/tours';
import { parseRecordKey, recordKey, type DraftState, type RecordKey } from '@/features/records/draft/changeSet';
import type {
  RecordEditability,
  RecordEditorConfig,
  RecordField,
  RecordOptionRef,
  RecordVerdict,
} from '@/features/records/editor/types';

import { TRIP_TYPE } from '@/api/tours';
import { formatWallDate } from '@/features/records/editor/format';
import { isStagedFile, isStoredFile, type StagedFiles } from '@/features/records/fields/widgets/stagedFiles';

import type { TourBaseline } from './baselines';
import {
  ALIAS_ENTITY,
  contextValueKind,
  DISPLAY_CURRENCY_LABELS,
  ID_TYPE_CHOICES,
  importRule,
  OBJECT_TITLES,
  PRECHECK,
  PRETRIP_ENTITY,
  TAGCAT_ENTITY,
  TOUR_ENTITY,
  TOUR_FIELDS,
  WAIVER_REQUIRED_PATH,
} from './catalog';
import {
  itineraryCascade,
  itineraryFormat,
  itineraryGroupOf,
  itineraryRecordOrder,
  itineraryRowLabel,
  type ItineraryLedgerContext,
} from '../itinerary/ledger';
import { DAYS_PATH, isItineraryRec, itineraryDefault, TOUR_KML } from '../itinerary/model';

import { managedTooltip } from '@/features/ext-ids/extIdFormat';
import { TAKE_OVER_EFFECT, handBackEffect } from '@/features/ext-ids/ownership';

import { formatLength } from './length';
import { describeFields, sharedFieldWarnings } from './precheck';
import { fieldListSummary } from '@/features/precheck/model';
import { ALIAS_MANAGED, COLLECTION_BY_ENTITY, COLLECTION_BY_MEMBERSHIP, type CollectionSpec } from './collections';
import { useTourLocalPlan } from './localPlan';
import { TourNames } from './names';
import { PRECHECK_WIDGETS } from './panels/PreCheckBlock';
import { useTourApply } from './useTourApply';
import { LAT_PATH, LNG_PATH, TOUR_STATIC_WIDGETS, tourPickerWidgets } from './widgets';

/** The name of the system that feeds an import-bound tour ("Bokun"); null = not fed. */
export function tourManagedBy(detail: Pick<TourDetail, 'import_summary'>): string | null {
  const s = detail.import_summary;
  if (!s?.is_import_bound) return null;
  const src = s.sources[0];
  return src?.name || src?.code || 'Imports';
}

export interface TourEditorConfigInput {
  detail: TourDetail;
  baseline: TourBaseline;
  files: StagedFiles;
  /** The operator may edit tours here (console_tours_manage, an owning operator, not
   *  deleted). */
  canEdit: boolean;
  onFocusMissing?: (rec: RecordKey, path: string) => void;
  /** Category names (stored rows and picks for new ones). */
  names?: TourNames;
  /** The operator may see booked guests (console_bookings_view): the pre-trip items'
   *  individual users are picked from them. */
  canSeeGuests?: boolean;
}

/** Why the tag-category rows are read-only (the operator config), or null. */
export function tagCategoriesLockReason(detail: Pick<TourDetail, 'tag_categories_editable'>): string | null {
  return detail.tag_categories_editable
    ? null
    : 'This operator does not allow per-tour tag categories (operator config allow_tour_tag_categories_modification) — a save would drop the changes';
}

/** A collection row's name in a sentence: its value (an alias's ID, a row's category),
 *  else the new row's placeholder `NEW-<n>`. */
export function rowName(
  rec: RecordKey,
  valueOf: (rec: RecordKey, path: string) => unknown,
  names?: TourNames,
): string {
  const { entity, id } = parseRecordKey(rec);
  if (entity === ALIAS_ENTITY) {
    const v = valueOf(rec, 'alias.custom_id');
    if (typeof v === 'string' && v.trim()) return v.trim();
  } else if (entity === TAGCAT_ENTITY) {
    const c = valueOf(rec, 'tagcat.category');
    if (typeof c === 'number') return names ? names.categoryLabel(c) : `#${c}`;
  } else if (entity === PRETRIP_ENTITY) {
    const item = valueOf(rec, 'pretrip.item');
    if (typeof item === 'number') return names ? names.itemLabel(item) : `Item #${item}`;
  }
  return id < 0 ? `NEW-${-id}` : `#${id}`;
}

const ROW_NOUN: Record<string, string> = {
  [ALIAS_ENTITY]: 'Alias',
  [TAGCAT_ENTITY]: 'Tag category',
  [PRETRIP_ENTITY]: 'Pre-trip item',
};

const MEMBERSHIP_NOUNS: Record<string, [string, string]> = {
  additional_custom_ids: ['ID', 'IDs'],
  tag_categories: ['category', 'categories'],
  pre_trip_items: ['item', 'items'],
};

/** Why the length cannot be set by hand on this tour, or null. */
export function lengthLockReason(detail: Pick<TourDetail, 'trip_type'>): string | null {
  return detail.trip_type === TRIP_TYPE.BUS_TRIP || detail.trip_type === TRIP_TYPE.EXHIBITION
    ? null
    : 'Derived from the itinerary — only bus trips and exhibitions set a length by hand';
}

const LEAD_TRIO = [PRECHECK('lead_booker_fields'), PRECHECK('lead_tag_categories_enabled'), PRECHECK('lead_selected_tag_categories')];
const PARTY_TRIO = [PRECHECK('party_member_fields'), PRECHECK('party_tag_categories_enabled'), PRECHECK('party_selected_tag_categories')];
const PRECHECK_LINKED = new Set([...LEAD_TRIO, ...PARTY_TRIO, PRECHECK('apply_same_fields_to_party_members')]);

const OPERATOR_DEFAULT_KEYS = new Set([
  'show_all_users_to_guides_in_nonbespoke_tours',
  'selfreg_verify_email',
  'selfreg_verify_phone',
]);

/** A membership list as the ledger reads it: "2 IDs" for the stored list, "3 (+ NEW-1,
 *  − GYG-1)" for a changed one, "2, reordered" when only the order moved. */
function formatMembership(
  spec: CollectionSpec,
  value: unknown,
  stored: unknown,
  label: (id: number) => string,
): string {
  const now = Array.isArray(value) ? (value as number[]) : [];
  const was = Array.isArray(stored) ? (stored as number[]) : [];
  const noun = MEMBERSHIP_NOUNS[spec.key] ?? ['row', 'rows'];
  const n = `${now.length}`;
  if (JSON.stringify(now) === JSON.stringify(was)) return `${n} ${now.length === 1 ? noun[0] : noun[1]}`;
  const added = now.filter((id) => !was.includes(id)).map((id) => `+ ${label(id)}`);
  const removed = was.filter((id) => !now.includes(id)).map((id) => `− ${label(id)}`);
  const parts = [...added, ...removed];
  return parts.length > 0 ? `${n} (${parts.join(', ')})` : `${n}, reordered`;
}

function verdictFor(field: RecordField, managedBy: string | null): RecordVerdict | null {
  if (!managedBy) return null;
  const rule = importRule(field.key);
  if (rule === 'forced') {
    return { verdict: 'overwrites', evidence: `${managedBy} imports reset this on every run unless the XML sets it` };
  }
  if (rule === 'owned') return { verdict: 'may_overwrite', evidence: `${managedBy} imports may overwrite this on their next run` };
  return null;
}

/** The pure half of the config (everything but the hooks and pickers), for tests. */
export function tourEditorConfigParts({
  detail,
  baseline,
  canEdit,
  onFocusMissing,
  names = new TourNames(detail),
  canSeeGuests = false,
}: Omit<TourEditorConfigInput, 'files'>): Omit<RecordEditorConfig, 'usePreview' | 'useApply' | 'widgets'> {
  const tourRec = recordKey(TOUR_ENTITY, detail.id);
  const managedBy = tourManagedBy(detail);
  const tour = baseline.paths[tourRec] ?? {};
  const tagcatLock = tagCategoriesLockReason(detail);
  /** A path's value in a draft state: the entry's, else the stored one, else the
   *  default of a new row. */
  const valueIn = (state: DraftState | undefined) => (rec: RecordKey, path: string) => {
    const entry = state?.entries[rec]?.[path];
    if (entry) return entry.to;
    const stored = baseline.paths[rec]?.[path];
    if (stored !== undefined) return stored;
    return COLLECTION_BY_ENTITY.get(parseRecordKey(rec).entity)?.defaults[path];
  };
  const memberLabel = (spec: CollectionSpec, id: number) =>
    rowName(recordKey(spec.entity, id), valueIn(undefined), names);
  const itinerary: ItineraryLedgerContext = { baselines: baseline.paths, tourRec, names };
  const isItineraryPath = (rec: RecordKey, path: string) =>
    isItineraryRec(rec) || (rec === tourRec && (path === DAYS_PATH || path === TOUR_KML));

  const editabilityOf = (rec: RecordKey, field: RecordField, state?: DraftState): RecordEditability => {
    if (!canEdit) return { editable: false, reason: 'Needs the console_tours_manage permission' };
    if (field.entity === ALIAS_ENTITY && field.key !== ALIAS_MANAGED) {
      const v = valueIn(state);
      if (v(rec, ALIAS_MANAGED) === true) {
        const integration = v(rec, 'alias.integration');
        const account = v(rec, 'alias.restech_account');
        return {
          editable: false,
          reason: managedTooltip(typeof integration === 'string' ? integration : null, typeof account === 'string' ? account : null),
        };
      }
    }
    if (tagcatLock && (field.entity === TAGCAT_ENTITY || field.key === 'tag_categories.order')) {
      return { editable: false, reason: tagcatLock };
    }
    if (rec === tourRec && field.key.startsWith('context.') && field.key !== WAIVER_REQUIRED_PATH) {
      // A list or an object under a text key cannot be edited as text without losing its
      // shape; numbers and booleans read as their text (`contextBaseline`).
      if (contextValueKind(tour[field.key]) === 'structured') {
        return { editable: false, reason: 'Stored as structured data (JSON) — it cannot be edited as text here' };
      }
    }
    if (field.key === 'length' || field.key === 'length_is_manual') {
      const reason = lengthLockReason(detail);
      if (reason) return { editable: false, reason };
    }
    if (field.key === 'pretrip.users' && !detail.is_bespoke) {
      return { editable: false, reason: 'Individual users can be targeted on fixed-date tours only' };
    }
    if (field.key === 'pretrip.users' && !canSeeGuests) {
      return { editable: false, reason: 'Picking users booked on the tour needs console_bookings_view (guest data)' };
    }
    return { editable: true };
  };

  const formatValue = (field: RecordField, rec: RecordKey, value: unknown): string | undefined => {
    if (isItineraryPath(rec, field.key)) {
      const text = itineraryFormat(field, rec, value, itinerary);
      if (text !== undefined) return text;
    }
    if (field.widget === 'image') {
      if (isStagedFile(value)) return `${value.name} (new file)`;
      if (isStoredFile(value)) return 'Current image';
      if (value === null || value === undefined) return 'No image';
    }
    if (field.widget === 'coordinates' && typeof value === 'number') return String(value);
    if (field.key === 'launch_policy' && (value === null || value === undefined)) {
      const fallback = detail.operator_default_launch_policy?.name;
      return fallback ? `Operator default (${fallback})` : 'Operator default (none set)';
    }
    const membership = COLLECTION_BY_MEMBERSHIP.get(field.key);
    if (membership) {
      return formatMembership(membership, value, tour[field.key], (id) => memberLabel(membership, id));
    }
    if (field.entity === ALIAS_ENTITY) {
      if (field.key === ALIAS_MANAGED) return value === true ? 'an import' : 'ops';
      if (field.key === 'alias.use_for_matching') return value === false ? 'parked' : 'matching';
      if (field.key === 'alias.api_queryable') return value === false ? 'not queryable' : 'queryable';
      if (field.key === 'alias.id_type') return ID_TYPE_CHOICES.find((c) => c.value === value)?.label ?? String(value ?? '—');
      if (field.key === 'alias.integration' && (value === null || value === undefined || value === '')) return 'any system';
      if (field.key === 'alias.restech_account' && (value === null || value === undefined || value === '')) return 'default account';
    }
    if (field.entity === TAGCAT_ENTITY) {
      if (field.key === 'tagcat.category') return names.categoryLabel(value);
      if (field.widget === 'boolean') return value === true ? 'on' : 'off';
      if (field.key === 'tagcat.max_tags' && value === 0) return '0 (unlimited)';
    }
    if (field.entity === PRETRIP_ENTITY) {
      if (field.key === 'pretrip.item') return names.itemLabel(value);
      if (field.widget === 'boolean') return value === true ? 'on' : 'off';
    }
    if (field.key === 'vehicle_route_set' && (value === null || value === undefined)) return 'No line set';
    if (OPERATOR_DEFAULT_KEYS.has(field.key) && (value === null || value === undefined)) return 'Operator default';
    if (field.key === WAIVER_REQUIRED_PATH) return value === true ? 'Required' : 'Not set';
    if (field.key.startsWith('context.') && value !== null && value !== undefined && typeof value !== 'string') {
      return JSON.stringify(value);
    }
    if (field.key === 'effective_display_currency') {
      return typeof value === 'number' ? (DISPLAY_CURRENCY_LABELS[value] ?? `#${value}`) : undefined;
    }
    if (field.key === 'end_date') return typeof value === 'string' && value ? formatWallDate(value) : undefined;
    if (field.key === 'length') return formatLength(value);
    if (field.key === 'length_is_manual') return value === true ? 'set by hand' : 'derived';
    if (field.key === PRECHECK('lead_booker_fields') || field.key === PRECHECK('party_member_fields')) {
      return describeFields(value);
    }
    if (field.key === PRECHECK('lead_selected_tag_categories') || field.key === PRECHECK('party_selected_tag_categories')) {
      const codes = Array.isArray(value) ? value.map(String) : [];
      return codes.length > 0 ? codes.join(', ') : 'none';
    }
    if (field.key === PRECHECK('apply_same_fields_to_party_members')) return value === true ? 'Yes' : 'No';
    if (field.key === PRECHECK('waivers')) {
      const n = Array.isArray(value) ? value.length : 0;
      return n === 0 ? 'no waivers' : `${n} ${n === 1 ? 'waiver' : 'waivers'}`;
    }
    return undefined;
  };

  // A pre-check list whose counts did not move says what moved in it, not "3 fields → 3
  // fields".
  const describeChange = (field: RecordField, rec: RecordKey, from: unknown, to: unknown): string | undefined =>
    rec === tourRec && (field.key === PRECHECK('lead_booker_fields') || field.key === PRECHECK('party_member_fields'))
      ? (fieldListSummary(from, to) ?? undefined)
      : undefined;

  const rowLabel = (
    rec: RecordKey,
    path: string,
    field: RecordField | undefined,
    state: DraftState,
  ): string | undefined => {
    if (isItineraryPath(rec, path)) return itineraryRowLabel(rec, path, field, state, itinerary);
    const noun = ROW_NOUN[parseRecordKey(rec).entity];
    if (noun) return `${noun} ${rowName(rec, valueIn(state), names)} · ${field?.label ?? path}`;
    if (rec !== tourRec) return undefined;
    // The coordinates slot is labelled "Coordinates"; its two rows say which half moved.
    if (path === LAT_PATH) return 'Meeting point · Latitude';
    if (path === LNG_PATH) return 'Meeting point · Longitude';
    const head = path.split('.')[0]!;
    const title = path.includes('.') ? OBJECT_TITLES[head] : undefined;
    if (!title || !field) return undefined;
    return field.label.toLowerCase().startsWith(title.toLowerCase()) ? field.label : `${title} · ${field.label}`;
  };

  // The coordinates are one value to the operator: reverting either half reverts both.
  // Reverting a membership list takes back the rows it added, with their entries; an
  // alias's system and account are one choice.
  const cascadeTargets = (rec: RecordKey, path: string, state: DraftState) => {
    if (isItineraryPath(rec, path)) return itineraryCascade(rec, path, state, itinerary);
    if (rec === tourRec && (path === LAT_PATH || path === LNG_PATH)) {
      return [{ rec, path: path === LAT_PATH ? LNG_PATH : LAT_PATH }];
    }
    // The length and whether it is set by hand are one choice.
    if (rec === tourRec && (path === 'length' || path === 'length_is_manual')) {
      return [{ rec, path: path === 'length' ? 'length_is_manual' : 'length' }];
    }
    // A scope's fields, its tag-category questions and their switch move together (the
    // questions are entries of the field list); with "same fields for party members" on,
    // the party's follow the lead booker's.
    if (rec === tourRec && PRECHECK_LINKED.has(path)) {
      const lead = LEAD_TRIO.includes(path);
      const applySame = state.entries[rec]?.[PRECHECK('apply_same_fields_to_party_members')];
      const partyFollows = path === PRECHECK('apply_same_fields_to_party_members') || (lead && applySame?.to === true);
      const linked = [...(lead ? LEAD_TRIO : []), ...(!lead || partyFollows ? PARTY_TRIO : [])];
      return linked.filter((p) => p !== path && state.entries[rec]?.[p]).map((p) => ({ rec, path: p }));
    }
    const membership = rec === tourRec ? COLLECTION_BY_MEMBERSHIP.get(path) : undefined;
    if (membership) {
      const to = state.entries[rec]?.[path]?.to;
      const added = (Array.isArray(to) ? (to as number[]) : []).filter((id) => id < 0);
      return added.flatMap((id) => {
        const row = recordKey(membership.entity, id);
        return Object.keys(state.entries[row] ?? {}).map((p) => ({ rec: row, path: p }));
      });
    }
    if (path === 'alias.integration') return [{ rec, path: 'alias.restech_account' }];
    if (path === 'alias.restech_account') return [{ rec, path: 'alias.integration' }];
    // Undoing a take-over hands the row back to its import, which the save would not
    // edit: the row's edits go with it.
    if (path === ALIAS_MANAGED && baseline.paths[rec]?.[ALIAS_MANAGED] === true) {
      return Object.keys(state.entries[rec] ?? {})
        .filter((p) => p !== ALIAS_MANAGED)
        .map((p) => ({ rec, path: p }));
    }
    return [];
  };

  // What a change sets off beyond its field, said under the field and on its ledger row.
  const changeWarnings = (rec: RecordKey, path: string, to: unknown): string[] => {
    if (path === ALIAS_MANAGED) {
      const stored = baseline.paths[rec]?.[ALIAS_MANAGED] === true;
      if (to === false && stored) return [`Taken over: ${TAKE_OVER_EFFECT}`];
      if (to === true && !stored) {
        const integration = baseline.paths[rec]?.['alias.integration'];
        const account = baseline.paths[rec]?.['alias.restech_account'];
        return [
          handBackEffect(typeof integration === 'string' ? integration : null, typeof account === 'string' ? account : null),
        ];
      }
      return [];
    }
    if (rec !== tourRec) return [];
    if (path === 'disable_internal_chat' && to === true) {
      return [
        detail.internal_chat_disabled_by_operator
          ? 'The operator already turns internal chat off for every tour — this only keeps it off here'
          : 'Saving retires this tour’s guides & staff conversation: its participants are removed, its history is kept',
      ];
    }
    if (path === 'disable_internal_chat' && to === false && detail.internal_chat_disabled_by_operator) {
      return ['The operator turns internal chat off for every tour — clearing this does not turn it on'];
    }
    if (path === 'start_date' && detail.is_bespoke) {
      const total = detail.user_stats?.total ?? 0;
      return total > 0 ? [`Saving moves the tour’s ${total} ${total === 1 ? 'booking' : 'bookings'} to the new date`] : [];
    }
    if (path === 'departures' && Array.isArray(to) && to.length === 0) {
      return ['With no departure, saving creates one from the tour’s start time'];
    }
    // Pre-check fields are shared by the operator's tours (keyed by id): a rename reaches
    // every tour.
    if (path === PRECHECK('lead_booker_fields') || path === PRECHECK('party_member_fields')) {
      return sharedFieldWarnings(to, tour[path], detail.tenant?.name);
    }
    // These three cannot be written back to null: once set, the operator default no
    // longer applies.
    if (OPERATOR_DEFAULT_KEYS.has(path) && (tour[path] === null || tour[path] === undefined) && to !== null && to !== undefined) {
      return ['Once changed, this no longer follows the operator default — it cannot be set back to it here'];
    }
    return [];
  };

  const defaultValueOf = (rec: RecordKey, path: string): unknown => {
    const entity = rec.slice(0, rec.lastIndexOf(':'));
    return COLLECTION_BY_ENTITY.get(entity)?.defaults[path] ?? itineraryDefault(rec, path);
  };

  const optionRefs: Record<string, RecordOptionRef[]> = {
    launch_policy: [detail.launch_policy, detail.default_launch_policy, detail.operator_default_launch_policy]
      .filter((r): r is NonNullable<typeof r> => Boolean(r))
      .map((r) => ({ id: r.id, label: r.name ?? `#${r.id}` })),
    'alias.tour_user_tags': detail.additional_custom_ids.flatMap((a) =>
      (a.tour_user_tags ?? []).map((t) => ({ id: t.id, label: t.name ?? `#${t.id}`, group: t.category ?? 'Other' })),
    ),
    'alias.targets': detail.additional_custom_ids.flatMap((a) =>
      (a.targets ?? []).map((t) => ({ id: t.id, label: t.name ?? `Tour #${t.id}`, group: 'Tours' })),
    ),
    atag: detail.atag.map((r) => ({ id: r.id, label: r.name ?? `#${r.id}`, group: 'Access tags' })),
    tags: detail.tags.map((r) => ({ id: r.id, label: r.name ?? `#${r.id}` })),
    crosssells: detail.crosssells.map((r) => ({ id: r.id, label: r.name ?? `#${r.id}`, group: 'Packages' })),
    departures: (detail.departures ?? [])
      .filter((d) => d.tag)
      .map((d) => ({ id: d.tag!.id, label: d.tag!.name, group: 'Departures' })),
    'pretrip.roles': detail.pre_trip_items.flatMap((r) => r.roles.map((x) => ({ id: x.id, label: x.name ?? `Role #${x.id}`, group: 'Roles' }))),
    'pretrip.agroups': detail.pre_trip_items.flatMap((r) =>
      r.agroups.map((x) => ({ id: x.id, label: x.name ?? `Group #${x.id}`, group: 'Access groups' })),
    ),
    'pretrip.users': detail.pre_trip_items.flatMap((r) => r.users.map((x) => ({ id: x.id, label: x.name ?? `User #${x.id}`, group: 'Users' }))),
    ...(detail.vehicle_route_set
      ? { vehicle_route_set: [{ id: detail.vehicle_route_set.id, label: detail.vehicle_route_set.name ?? `#${detail.vehicle_route_set.id}` }] }
      : {}),
  };

  return {
    fields: TOUR_FIELDS,
    options: [],
    actions: [],
    effects: [],
    timezone: detail.start_timezone,
    recordNoun: 'tour',
    managedBy,
    ...(managedBy ? { managedNote: `${managedBy} imports may rewrite the fields with the sync icon on their next run` } : {}),
    verdictOf: (_rec, field) => verdictFor(field, managedBy),
    editabilityOf,
    // One group: the tour, its aliases and its tag-category rows are one record to the
    // operator (row labels name the row).
    groupOf: (rec: RecordKey, state?: DraftState) =>
      itineraryGroupOf(rec, state, itinerary) ??
      (rec === tourRec || ROW_NOUN[parseRecordKey(rec).entity] ? { id: 'tour', title: detail.name } : { id: rec, title: rec }),
    rowLabel,
    cascadeTargets,
    defaultValueOf,
    changeWarnings,
    // The tour, then the itinerary's records in its stored order — its day groups follow
    // the days.
    recordOrder: [tourRec, ...itineraryRecordOrder(baseline.paths, tourRec)],
    formatValue,
    describeChange,
    optionRefs,
    // Saving asks first when it retires a chat thread, moves every booking's date, or
    // changes who maintains an external ID.
    confirmFollowUps: ['internal_chat_off', 'bookings_move', 'ext_id_take_over', 'ext_id_hand_back'],
    description: false,
    ...(onFocusMissing ? { onFocusMissing } : {}),
  };
}

/** The tour editor's `RecordEditorConfig` — stable while its inputs are. */
export function useTourRecordEditorConfig(input: TourEditorConfigInput): RecordEditorConfig {
  const { detail, baseline, files, canEdit, onFocusMissing, names, canSeeGuests = false } = input;
  const tenantId = detail.tenant?.id ?? 0;
  const tourId = detail.id;
  const widgets = useMemo(
    () => ({ ...TOUR_STATIC_WIDGETS, ...PRECHECK_WIDGETS, ...tourPickerWidgets(tenantId, tourId) }),
    [tenantId, tourId],
  );
  // The save reads the baseline of the latest loaded detail at save time.
  const baselineRef = useRef(baseline);
  baselineRef.current = baseline;
  const planCtx = useMemo(
    () => ({ base: baseline, detail, files, operatorName: detail.tenant?.name ?? null }),
    [baseline, detail, files],
  );
  return useMemo<RecordEditorConfig>(
    () => ({
      ...tourEditorConfigParts({ detail, baseline, canEdit, onFocusMissing, canSeeGuests, ...(names ? { names } : {}) }),
      widgets,
      usePreview: (body) => useTourLocalPlan(body, planCtx),
      useApply: ({ onSaved }) => useTourApply({ tourId: detail.id, baselineRef, files, onSaved }),
    }),
    [detail, baseline, canEdit, onFocusMissing, names, canSeeGuests, widgets, planCtx, files],
  );
}
